import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PinModal from '../components/PinModal';
import { Toast } from '../components/Toast';

function Trending() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('week');
  const [pins, setPins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPinId, setSelectedPinId] = useState(null);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    loadPins();
  }, [activeTab]);

  const loadPins = async () => {
    setIsLoading(true);
    try {
      let endpoint = '';
      if (activeTab === 'week') {
        endpoint = '/pins/trending';
      } else if (activeTab === 'likes') {
        endpoint = '/pins/top/liked';
      } else if (activeTab === 'views') {
        endpoint = '/pins/top/viewed';
      }

      const response = await fetch(API_BASE_URL + endpoint);
      if (response.ok) {
        const data = await response.json();
        setPins(data);
      } else {
        Toast.error('Không thể tải dữ liệu');
      }
    } catch (error) {
      console.error('Error loading pins:', error);
      Toast.error('Lỗi khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50/30 to-red-50/30 dark:from-gray-900 dark:via-orange-900/10 dark:to-red-900/10">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 border-4 border-orange-200 dark:border-orange-900 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 font-medium text-lg">Đang tải trending...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50/30 to-red-50/30 dark:from-gray-900 dark:via-orange-900/10 dark:to-red-900/10">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header with gradient and animation */}
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl blur-xl opacity-50 animate-pulse-slow"></div>
              <div className="relative bg-gradient-to-br from-orange-500 to-red-600 p-3 rounded-2xl shadow-lg">
                <span className="material-symbols-rounded text-4xl sm:text-5xl text-white">
                  whatshot
                </span>
              </div>
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 dark:from-orange-400 dark:via-red-400 dark:to-pink-400 bg-clip-text text-transparent">
                Trending
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base mt-1">
                Khám phá những prompt hot nhất 🔥
              </p>
            </div>
          </div>
        </div>

        {/* Tabs with better design */}
        <div className="mb-8 overflow-x-auto animate-slide-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex gap-3 min-w-max pb-2">
            <button
              onClick={() => setActiveTab('week')}
              className={`group relative flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all transform hover:scale-105 ${
                activeTab === 'week' 
                  ? 'bg-gradient-to-br from-orange-500 to-red-600 text-white shadow-xl shadow-orange-500/30' 
                  : 'bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800'
              }`}
            >
              <span className={`material-symbols-rounded text-[22px] transition-transform group-hover:scale-110 ${activeTab === 'week' ? 'animate-bounce-in' : ''}`}>
                trending_up
              </span>
              <span className="whitespace-nowrap">Tuần này</span>
              {activeTab === 'week' && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-1 bg-white rounded-full"></div>
              )}
            </button>

            <button
              onClick={() => setActiveTab('likes')}
              className={`group relative flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all transform hover:scale-105 ${
                activeTab === 'likes' 
                  ? 'bg-gradient-to-br from-red-500 to-pink-600 text-white shadow-xl shadow-red-500/30' 
                  : 'bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-2 border-red-200 dark:border-red-800'
              }`}
            >
              <span className={`material-symbols-rounded text-[22px] transition-transform group-hover:scale-110 ${activeTab === 'likes' ? 'animate-heartbeat' : ''}`}
                style={{
                  fontVariationSettings: activeTab === 'likes' ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                }}
              >
                favorite
              </span>
              <span className="whitespace-nowrap">Nhiều like nhất</span>
              {activeTab === 'likes' && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-1 bg-white rounded-full"></div>
              )}
            </button>

            <button
              onClick={() => setActiveTab('views')}
              className={`group relative flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all transform hover:scale-105 ${
                activeTab === 'views' 
                  ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-xl shadow-blue-500/30' 
                  : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800'
              }`}
            >
              <span className={`material-symbols-rounded text-[22px] transition-transform group-hover:scale-110 ${activeTab === 'views' ? 'animate-pulse-slow' : ''}`}>
                visibility
              </span>
              <span className="whitespace-nowrap">Nhiều view nhất</span>
              {activeTab === 'views' && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-1 bg-white rounded-full"></div>
              )}
            </button>
          </div>
        </div>

        {/* Results count */}
        {pins.length > 0 && (
          <div className="mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-md border border-gray-200 dark:border-gray-700">
              <span className="material-symbols-rounded text-[20px] text-orange-600 dark:text-orange-400">
                {activeTab === 'week' ? 'trending_up' : activeTab === 'likes' ? 'favorite' : 'visibility'}
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {pins.length} prompts
              </span>
            </div>
          </div>
        )}

        {/* Empty state with better design */}
        {pins.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-200 dark:border-gray-700 animate-fade-in">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-600 rounded-full blur-2xl opacity-20 animate-pulse-slow"></div>
              <span className="relative material-symbols-rounded text-8xl text-gray-300 dark:text-gray-600 block">
                search_off
              </span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-xl font-medium mb-2">
              Chưa có prompt nào
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              Hãy quay lại sau để xem trending mới nhất
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {pins.map((pin, index) => {
              const isTop3 = index < 3;
              const badgeColor = index === 0 
                ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 shadow-lg shadow-yellow-500/50' 
                : index === 1 
                ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-gray-800 shadow-lg shadow-gray-400/50' 
                : 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-lg shadow-orange-500/50';
              
              return (
                <div 
                  key={pin._id} 
                  className="relative group animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {/* Top 3 Badge with better design */}
                  {isTop3 && (
                    <div className={`absolute -top-2 -left-2 z-10 w-10 h-10 rounded-full flex items-center justify-center font-bold text-base shadow-2xl text-white ${badgeColor} animate-bounce-in ring-4 ring-white dark:ring-gray-900`}>
                      {index === 0 && <span className="material-symbols-rounded text-[20px]">trophy</span>}
                      {index === 1 && <span className="material-symbols-rounded text-[20px]">military_tech</span>}
                      {index === 2 && <span className="material-symbols-rounded text-[20px]">star</span>}
                    </div>
                  )}

                  <div
                    onClick={() => setSelectedPinId(pin._id)}
                    className="cursor-pointer rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={pin.image}
                        alt={pin.title}
                        className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      
                      {/* Gradient overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <div className="flex items-center justify-between gap-3 text-white">
                            <div className="flex items-center gap-1 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full">
                              <span className="material-symbols-rounded text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>favorite</span>
                              <span className="font-bold text-sm">{pin.likes || 0}</span>
                            </div>
                            <div className="flex items-center gap-1 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full">
                              <span className="material-symbols-rounded text-[18px]">visibility</span>
                              <span className="font-bold text-sm">{pin.views || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Rank number for non-top3 */}
                      {!isTop3 && (
                        <div className="absolute top-2 left-2 w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center font-bold text-sm text-white">
                          {index + 1}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-3">
                      <h3 className="font-bold text-sm text-gray-900 dark:text-white line-clamp-2 mb-2 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                        {pin.title}
                      </h3>
                      
                      {/* User info - clickable */}
                      {pin.user && (
                        <div 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/user/${pin.user}`);
                          }}
                          className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1.5 rounded-lg transition-colors"
                        >
                          <img
                            src={`https://api.dicebear.com/7.x/notionists/svg?seed=${pin.user}&backgroundColor=e9e9e9`}
                            alt={pin.user}
                            className="w-6 h-6 rounded-full bg-gray-100"
                          />
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">
                            {pin.user}
                          </span>
                        </div>
                      )}
                      
                      {pin.board && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-2 bg-gray-50 dark:bg-gray-700/50 px-2 py-1 rounded-lg">
                          <span className="material-symbols-rounded text-[14px]">
                            {pin.board.icon || 'folder'}
                          </span>
                          <span className="truncate font-medium">{pin.board.name}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between gap-3 text-xs">
                        <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <span className="material-symbols-rounded text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>favorite</span>
                          <span className="font-bold">{pin.likes || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                          <span className="material-symbols-rounded text-[16px]">visibility</span>
                          <span className="font-bold">{pin.views || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedPinId && (
          <PinModal pinId={selectedPinId} onClose={() => setSelectedPinId(null)} />
        )}
      </div>
    </div>
  );
}

export default Trending;
